import React, { Dispatch } from "react"
import { Button, Col, Form, InputGroup, ListGroup, Modal, OverlayTrigger, Popover, Spinner } from "react-bootstrap"
import MaskedInput from "react-maskedinput"
import { toast } from "react-toastify"
import { BehaviorSubject, combineLatest, concat, of, Subject, Subscription } from "rxjs"
import { catchError, debounceTime, filter, map, skipWhile, switchMap } from "rxjs/operators"
import { FaAngleDown, FaInfoCircle } from "react-icons/fa"
import { ImplementationTypeGovt, PhoneRegexp, SiteDomain, ZipRegexp, CompanyNameRegexp, ProtectedSites, MaxFileNameLength, MaxImageFileSize, EmailRegexp } from "../../config/Constants"
import { APIError } from "../../api/ObservableFromFetch"
import BusinessType from "../../api/BusinessTypes/BusinessType"
import BusinessTypesGet from "../../api/BusinessTypes/BusinessTypesGet"
import Address from "../../api/Location/LocationModel/Address"
import Role from "../../api/Roles/Model/Role"
import UserRolesGet from "../../api/Roles/UserRolesGet"
import PrimaryIDType from "../../api/PrimaryIDTypes/PrimaryIDType"
import PrimaryIDTypesGet from "../../api/PrimaryIDTypes/PrimaryIDTypesGet"
import CompanyInactivate from "../../api/Companies/CompanyInactivate"
import { CompanyUpdateObservable, CompanyCreateObservable } from "../../api/Companies/CompanyUpdate"
import CompanyData from "../../api/Companies/CompanyModel/CompanyData"
import CompaniesGet, { CompaniesDetailsExportCsv } from "../../api/Companies/CompaniesGet"
import { StateObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import State from "../../api/OrderRequests/OrderRequestsModel/State"
import AddressInput from "../FormattedInputs/AddressInput"
import PhoneNumberMaskedInput from "../FormattedInputs/PhoneNumberMaskedInput"
import StateSelect from "../FormattedInputs/StateSelect"
import ActionButton from "../ActionButton"
import AddressMatchModal, { AddressData } from "../AddressMatchModal"
import ConfirmationModal from "../ConfirmationModal"
import OrderSecurityOverride from "../Administration/OrderSecurityOverride"
import OrderSecurity from "../Invitation/OrderSecurity"
import LabAccountSelection from "./LabAccountSelection"
import { isCompanyAdmin, isPortalAdmin, isSiteAdmin } from "../../authentication/isAdminRole"
import { AppState, WorkforceClinicPortal } from "../../AppState"
import { connect } from "react-redux"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import CompanyEditData from "../../api/Companies/CompanyModel/CompanyEditData"
import SubscriptionPlan from "../../api/Administration/Model/SubscriptionPlan"
import SubscriptionModal from "./SubscriptionModal"
import { AsyncTypeahead } from "react-bootstrap-typeahead"
import AutoFilterPortalsGetObservable from "../../api/Portals/AutoFilterPortalsGet"
import PortalsGetObservable from "../../api/Portals/PortalsGet"
import GetSubscriptionPlans from "../../api/Administration/GetSubscriptionPlans"
import PortalUpdate from "../../api/Portals/PortalUpdate"
import PortalEditData from "../../api/Portals/PortalModel/PortalEditData"
import PortalData from "../../api/Portals/PortalModel/PortalData"
import PortalInactivateObservable from "../../api/Portals/PortalInactivate"
import { GetSelectedPortal, getUserNameForSelectedCompany, SelectedCompanyName, SelectedPortalName } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { RouteComponentProps } from "react-router"
import { AddCompanyForPortalRoute, AddNewCompanyRoute, CompanyDetailsRoute, Home, MyOrganizationRoute, PortalAdministrationRoute, PortalDetailsRoute } from "../../config/Routes"
import ManageImages from "./ManageImages"
import CompanyImport from "./CompanyImport"
import UserAccessSettings from "../../authentication/UserAccessSettings"
import { BuildUserAccess } from "../../authentication/BuildUserAccess"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import TimeZoneListGet from "../../api/Administration/TimeZoneListGet"
import TimeZoneData from "../../api/Administration/Model/TimeZoneData"
import PortalFields from "../Invitation/Portal/PortalFields";
import PortalAdminData from "../../api/Portals/PortalModel/PortalAdminData"
import { PortalInvitation, PortalInviteData } from "../../api/Invitation/InvitationCreate";
import LabAccountListGet from "../../api/Administration/LabAccountListGet"
import { EditLocationInformation } from "./EditLocationInformation"
import GetAccountsByNameObservable from "../../api/Companies/GetAccountsByName"
import Checkbox from "../Checkbox"
import { CompanyCodeCheck, CompanyNameCheck, UrlCheck } from "../../api/Invitation/InformationGet"
import { Addresses } from "../../api/OrderRequests/OrderRequestsModel/Addresses"
import { IsOnTrack } from "../../App";
import { CompanyActivationObservable, PortalActivationObservable } from "../../api/Administration/ActivationProcess"
import FileSaver from "file-saver"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import DeviceSelection from "./DeviceSelection"
import { ValidZipCode } from "../../formatters/FormatZipCode"
import LocationImport from "./LocationImport"
import AdditionalTesting from "./CompanyInformation/AdditionalTesting"
import { WorkforceReloadPortalsCompanies } from "../../actions/WorkforceClinicAction"
import { Action } from "redux"
import IntegrationTypeSelect from "./CompanyInformation/IntegrationTypeSelect"
import ToggleSwitch from "../FormattedInputs/ToggleSwitch"


export type CompanyInformationRouteParams = { name?: string, companyName?: string }

export interface CompanyInformationProps extends RouteComponentProps<CompanyInformationRouteParams> {
  portalMode?: boolean
  isNew?: boolean
}

interface CompanyInformationAuthProps {
  companyName?: string
  portalName?: string
  portal?: WorkforceClinicPortal
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
  isSiteAdmin?: boolean
  userAccess?: UserAccessSettings
  userName?: string
}

interface CompanyInformationDispatchProps {
  reload: () => void
}

interface CompanyInformationData {
  name: string
  code: string
  streetAddress1: string
  streetAddress2: string
  city: string
  state: string
  zip: string
  province?: string
  country?: string
  timeZoneID: number
  webSiteURL?: string
  fax: string
  officePhone: string
  contactDerPhone: string
  contactDerFirstName: string
  contactDerLastName: string
  contactDerEmail: string
  contactPhone: string
  contactFirstName: string
  contactLastName: string
  businessTypeId?: number
  portalAdminRoleID: number | string
  primaryIDType: number
  companyData?: CompanyData
  labAcctRequired: boolean
  inactive: boolean
  integrationType: string
}

interface PortalInformationData {
  implementationID?: number
  name: string
  code: string
  streetAddress1: string
  streetAddress2: string
  city: string
  state: string
  zip: string
  officePhone: string
  isTPA?: boolean
  subscriptionPlanID: number
  subscriptionPlan?: SubscriptionPlan
  implementationType: string
  portalData?: PortalData
  portalAdminData?: PortalAdminData
  url: string
  mroFileContent?: string
  inactive: boolean
  unmaskDonorID: number
  NoLabAccount : number
  generateResultExceptions: boolean
  hideAuthESS: boolean
}

type InformationData = CompanyInformationData & PortalInformationData
interface CompanyInformationState extends InformationData {
  implementationID?: number
  showInactivateConfirmation: boolean
  showOrderSecurityOverride: boolean
  showSubscriptions: boolean
  showManageImages: boolean
  formValidated: boolean
  businessTypesLoading: boolean
  businessTypes: BusinessType[]
  loading: boolean
  labAccountsLoading: boolean
  inactivating: boolean
  activating: boolean
  saving: boolean
  rolesLoading: boolean
  roles?: Role[]
  primaryIDTypesLoading: boolean
  primaryIDTypes?: PrimaryIDType[]
  statesLoading: boolean
  states: State[]
  subscriptionPlansLoading: boolean
  subscriptionPlans: SubscriptionPlan[]
  retrievedCompanies?: CompanyData[]
  retrievedPortals?: PortalData[]
  retrievedPortalPredictions?: string[]
  showOrderSecurity: boolean
  showLabAccountSelection: boolean
  showDevices: boolean
  showCompanyImport: boolean
  showLocationImport: boolean
  selectedCompany?: string
  allCompanies: AutoFilterCompany[]
  companies: string[]
  timeZones?: TimeZoneData[]
  timeZonesLoading: boolean
  mroFileName: string
  isPortalFieldsValid: boolean
  isNameValid: boolean
  isStreetAddress1Valid: boolean
  isCityValid: boolean
  isStateValid: boolean
  isZipValid: boolean
  isPhoneValid: boolean
  isBusinessTypeValid: boolean
  isPrimaryIDTypeValid: boolean
  activeLabAccountsCount: number
  showLocation: boolean
  showAdditionalTesting: boolean
  urlValid: boolean
  urlChecking: boolean
  urlAvailable?: boolean
  urlCheckMessage?: string
  isNameChecking: boolean
  isNameAvailable?: boolean
  isNameLengthValid: boolean
  isNameTextValid: boolean
  isCodeValid: boolean
  isCodeChecking: boolean
  isCodeAvailable?: boolean
  isMROFileSizeValid: boolean
  saved: boolean
  showNewCompanyModal: boolean
  loadingCsv: boolean
  integrationType: string
  generateResultExceptions: boolean
}

enum ImplementationType {
  EMBEDDED = "EMBEDDED",
  ServiceIndustry = "Service Industry",
  Employer = "Employer",
  OneSource = "OneSource",
  MRO = "MRO",
  NonTPA = "NonTPA",
  GOVT = "GOVT",
  TPA = "TPA",
  ACCIO = "ACCIO"
}

type T_ImplementationType = keyof typeof ImplementationType
const ImplementationTypes = (Object.keys(ImplementationType) as T_ImplementationType[])


interface NameData {
  value: string
  implementationID?: number
}

export class CompanyInformation extends React.PureComponent<CompanyInformationProps & CompanyInformationAuthProps & CompanyInformationDispatchProps, CompanyInformationState> {
  emptyData: InformationData = {
    name: "",
    code: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zip: "",
    officePhone: "",
    contactDerPhone: "",
    contactDerFirstName: "",
    contactDerLastName: "",
    contactDerEmail: "",
    contactPhone: "",
    contactFirstName: "",
    contactLastName: "",
    businessTypeId: undefined,
    portalAdminRoleID: "",
    primaryIDType: 0,
    companyData: undefined,
    isTPA: undefined,
    subscriptionPlanID: 0,
    subscriptionPlan: undefined,
    implementationType: ImplementationType.NonTPA,
    portalData: undefined,
    timeZoneID: 0,
    fax: "",
    portalAdminData: undefined,
    labAcctRequired: true,
    url: "",
    inactive: false,
    unmaskDonorID:0,
    NoLabAccount : 0,
    integrationType: "",
    generateResultExceptions: false,
    hideAuthESS: false
  }
  emptyState: CompanyInformationState = {
    ...this.emptyData,
    showInactivateConfirmation: false,
    showOrderSecurityOverride: false,
    showSubscriptions: false,
    showManageImages: false,
    formValidated: false,
    businessTypesLoading: false,
    businessTypes: [],
    loading: false,
    labAccountsLoading: false,
    inactivating: false,
    activating: false,
    saving: false,
    rolesLoading: false,
    primaryIDTypesLoading: false,
    statesLoading: false,
    states: [],
    subscriptionPlansLoading: false,
    subscriptionPlans: [],
    retrievedCompanies: undefined,
    showOrderSecurity: false,
    showLabAccountSelection: false,
    showDevices: false,
    showCompanyImport: false,
    showLocationImport: false,
    selectedCompany: this.props.match.params.companyName,
    companies: [],
    allCompanies: [],
    timeZonesLoading: false,
    activeLabAccountsCount: 0,
    showLocation: false,
    showAdditionalTesting: false,
    isNameValid: true,
    isStreetAddress1Valid: true,
    isPortalFieldsValid: false,
    isCityValid: true,
    isStateValid: true,
    isZipValid: true,
    isPhoneValid: true,
    isBusinessTypeValid: true,
    isPrimaryIDTypeValid: true,
    urlValid: true,
    urlChecking: false,
    isNameChecking: false,
    isNameLengthValid: true,
    isNameTextValid: true,
    isCodeChecking: false,
    isCodeValid: true,
    mroFileName: "",
    isMROFileSizeValid: true,
    saved: false,
    showNewCompanyModal: false,
    loadingCsv: false,
    integrationType: ""
  }
  state: CompanyInformationState = { ...this.emptyState }

  searchPortalsSubscription?: Subscription
  searchCompaniesSubscription?: Subscription
  searchPortalsInitialSubscription?: Subscription
  loadSubscription?: Subscription
  saveSubscription?: Subscription
  inactivateSubscription?: Subscription
  businessTypesSubscription?: Subscription
  userRolesSubscription?: Subscription
  primaryIDTypesSubscription?: Subscription
  statesSubscription?: Subscription
  subscriptionPlansSubscription?: Subscription
  loadCompaniesSubscription?: Subscription
  timeZoneSubscription?: Subscription
  loadLabAccountsSubscription?: Subscription
  urlExistsSubscription?: Subscription
  nameExistsSubscription?: Subscription
  codeExistsSubscription?: Subscription

  companyName$ = new Subject<string>()
  implementationName$ = new Subject<string>()
  searchImplementation$ = new Subject<string>()
  searchCompany$ = new Subject<string>()
  checkUrl$ = new Subject<string>()
  checkName$ = new Subject<NameData>()
  checkCode$ = new Subject<string>()

  typeahead = React.createRef<AsyncTypeahead<string>>()
  mroFile = React.createRef<HTMLInputElement>()

  implementationName?: string = this.props.match.params.name
  companyName?: string = this.props.match.params.companyName
  states$ = new BehaviorSubject<State[] | null>(null)

  componentDidMount() {
    this.load()
  }

  componentDidUpdate(prevProps: CompanyInformationProps, prevState: CompanyInformationState) {
    if (prevProps.match.params.name !== this.props.match.params.name) {
      this.load()
    }
    if (!this.props.portalMode) {
      if (this.state.companyData && !prevState.companyData) {
        this.loadLabAccounts()
      }
    }
  }

  componentWillUnmount() {
    this.searchPortalsSubscription && this.searchPortalsSubscription.unsubscribe()
    this.searchCompaniesSubscription && this.searchCompaniesSubscription.unsubscribe()
    this.searchPortalsInitialSubscription && this.searchPortalsInitialSubscription.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.loadCompaniesSubscription && this.loadCompaniesSubscription.unsubscribe()
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
    this.businessTypesSubscription && this.businessTypesSubscription.unsubscribe()
    this.primaryIDTypesSubscription && this.primaryIDTypesSubscription.unsubscribe()
    this.statesSubscription && this.statesSubscription.unsubscribe()
    this.subscriptionPlansSubscription && this.subscriptionPlansSubscription.unsubscribe()
    this.timeZoneSubscription && this.timeZoneSubscription.unsubscribe()
    this.loadLabAccountsSubscription && this.loadLabAccountsSubscription.unsubscribe()
    this.urlExistsSubscription && this.urlExistsSubscription.unsubscribe()
    this.nameExistsSubscription && this.nameExistsSubscription.unsubscribe()
    this.codeExistsSubscription && this.codeExistsSubscription.unsubscribe()
  }

  load = () => {
    this.setState({ statesLoading: true })
    this.statesSubscription = StateObservable().subscribe(
      (results: State[]) => {
        this.setState((prevState) => ({
          states: results,
          statesLoading: false,
          state: prevState.companyData
            ? this.mapState(prevState.companyData, results)
            : prevState.state,
        }));
        this.states$.next(results);
      },
      () => {
        this.setState({ statesLoading: false });
        this.states$.next(null);
      }
    );

    if (this.props.portalMode) {
      if (this.props.isSiteAdmin) {
        this.setState({ subscriptionPlansLoading: true })
        this.subscriptionPlansSubscription = GetSubscriptionPlans().subscribe(
          (results: SubscriptionPlan[]) => this.setState(prevState => ({
            subscriptionPlans: results,
            subscriptionPlansLoading: false,
            subscriptionPlan: prevState.portalData ? this.mapSubscriptionPlan(prevState.portalData, results) : undefined
          })),
          () => { this.setState({ subscriptionPlansLoading: false }) }
        )
      }

      this.searchPortalsSubscription = this.searchImplementation$.pipe(
        debounceTime(500),
        switchMap(name => concat(
          of({ loading: true }),
          AutoFilterPortalsGetObservable(name).pipe(
            map(result => ({ loading: false, result: result })),
            catchError(() => of({ loading: false }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: string[] }) => this.setState({ loading: o.loading, retrievedPortalPredictions: o.result }),
        (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
      )

      this.loadSubscription = combineLatest([
        this.implementationName$.pipe(map(name => name.trim())),
        this.states$.pipe(
          skipWhile(states => !states || states.length === 0)
        )
      ]).pipe(
        switchMap(([name, _]) => {
          return concat(
            of({ loading: true }),
            PortalsGetObservable(name, true).pipe(
              map(result => ({ loading: false, result })),
              catchError(() => of({ loading: false }))
            )
          );
        })
      ).subscribe(
        (o: { loading: boolean, result?: PortalData[] }) => this.setState({
          loading: o.loading,
          retrievedPortals: o.result && o.result.length > 1 ? o.result : undefined,
          ...this.mapPortalData(o.result)
        }),
        (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
      )
      if (!this.props.isSiteAdmin && this.props.portalName) {
        this.implementationName$.next(this.props.portalName)
      } else if (this.implementationName) {
        this.implementationName$.next(this.implementationName)
      }

      this.urlExistsSubscription = this.checkUrl$.pipe(
        map(name => `${name.trim()}.${SiteDomain}`),
        filter(name => this.isUrlValid(name)),
        switchMap(name => concat(
          of({ loading: true }),
          UrlCheck(name).pipe(
            map(result => ({ loading: false, result: true, message: result })),
            catchError((error) =>
              of({ loading: false, result: false, message: error?.errorData?.message || error.message })
            )
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: boolean, message?: string }) => this.setState({ urlChecking: o.loading, urlAvailable: o.result, urlCheckMessage: o.message }),
        (error) => this.handleAPIError(error, { urlChecking: false, urlAvailable: undefined, urlCheckMessage: undefined })
      )
    } else {
      this.setState({ rolesLoading: true, primaryIDTypesLoading: true, businessTypesLoading: true, timeZonesLoading: true })
      this.businessTypesSubscription = BusinessTypesGet().subscribe(
        (data) => this.setState({ businessTypes: data, businessTypesLoading: false }),
        (error) => this.handleAPIError(error, { businessTypesLoading: false })
      )
      this.userRolesSubscription = UserRolesGet().subscribe(
        (data: Role[]) => this.setState(prevState => ({
          roles: data,
          rolesLoading: false,
          portalAdminRoleID: prevState.companyData ? this.mapRole(prevState.companyData, data) || 0 : prevState.portalAdminRoleID
        })),
        (error) => this.handleAPIError(error, { rolesLoading: false })
      )
      this.primaryIDTypesSubscription = PrimaryIDTypesGet().subscribe(
        (data: PrimaryIDType[]) => this.setState(prevState => ({
          primaryIDTypes: data,
          primaryIDTypesLoading: false,
          primaryIDType: prevState.companyData ? this.mapPrimaryIDType(prevState.companyData, data) || 0 : prevState.primaryIDType
        })),
        (error) => this.handleAPIError(error, { primaryIDTypesLoading: false })
      )

      this.timeZoneSubscription = TimeZoneListGet().subscribe(
        (data: TimeZoneData[]) => this.setState({ timeZones: data, timeZonesLoading: false }),
        (error) => this.handleAPIError(error, { timeZonesLoading: false })
      )

      this.searchCompaniesSubscription = this.searchCompany$.pipe(
        debounceTime(500),
        switchMap(name => concat(
          of({ loading: true }),
          GetAccountsByNameObservable(name, true).pipe(
            map(result => ({ loading: false, result: result })),
            catchError(() => of({ loading: false }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: AutoFilterCompany[] }) => this.setState({ loading: o.loading, allCompanies: o.result || [], companies: o.result?.map(c => c.Name).filter((v, i, a) => a.indexOf(v) === i) || [] }),
        (error) => this.handleAPIError(error, { loading: false, companies: [], allCompanies: [] })
      )

      this.loadSubscription = this.companyName$.pipe(
        map(name => name.trim()),
        switchMap(name => concat(
          of({ loading: true }),
          CompaniesGet(name, this.state.allCompanies.find(c => c.Name === name)?.Inactive || false).pipe(
            map(result => ({ loading: false, result: result })),
            catchError((error) => of({ loading: false, error: error }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: CompanyData[], error?: Error | APIError }) => {
          if (o.error) this.handleAPIError(o.error, {})
          this.setState({
            loading: o.loading,
            retrievedCompanies: o.result && o.result.length > 1 ? o.result : undefined,
            isNameAvailable: true, isCodeChecking: false, isCodeValid: true, isCodeAvailable: undefined,
            ...this.mapCompanyData(o.result)
          })
        },
        (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
      )

      this.nameExistsSubscription = this.checkName$.pipe(
        map(data => { return {value: data.value.trim(), implementationID: data.implementationID} } ),
        filter(data => this.isCompanyNameValid(data.value)),
        switchMap(data => concat(
          of({ loading: true }),
          CompanyNameCheck(data.value, undefined, true, data.implementationID).pipe(
            map(result => ({ loading: false, result: result })),
            catchError(() => of({ loading: false }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: Addresses | null }) => this.setState({
          isNameChecking: o.loading,
          isNameAvailable: !o.result || o.result?.ParentID === this.state.companyData?.AccountID
        }),
        (error) => this.handleAPIError(error, { nameChecking: false, nameAvailable: undefined })
      )

      this.codeExistsSubscription = this.checkCode$.pipe(
        map(code => code),
        filter(code => code.trim().length > 0 && this.isCodeValid(code)),
        switchMap(code => concat(
          of({ loading: true }),
          CompanyCodeCheck(code).pipe(
            map(result => ({ loading: false, result: result })),
            catchError(() => of({ loading: false }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: string }) => this.setState({ isCodeChecking: o.loading, isCodeAvailable: !o.result }),
        (error) => this.handleAPIError(error, { codeChecking: false, codeAvailable: undefined })
      )

      this.loadSubscription = this.implementationName$.pipe(
        map(name => name.trim()),
        switchMap(name => concat(
          of({ loading: true }),
          PortalsGetObservable(name).pipe(
            map(result => ({ loading: false, result: result })),
            catchError(() => of({ loading: false }))
          )
        ))
      ).subscribe(
        (o: { loading: boolean, result?: PortalData[] }) => this.setState({
            loading: o.loading
          }, () => {
              if (o.result && o.result.length > 0 ) {
                const data = o.result[0]
                this.setState({ portalData: { ...data, IsOnTrack: data?.ImplementationType === ImplementationTypeGovt }})
              }
          }),
        (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
      )

      if (!this.isSiteAdminOrPortalAdmin() && this.props.companyName) {
        this.companyName$.next(this.props.companyName)
      } else if (this.implementationName) {
        this.implementationName$.next(this.implementationName)
      } else if (this.companyName) {
        this.setState({companies: [this.companyName], selectedCompany: this.companyName, showNewCompanyModal: true})
        this.companyName$.next(this.companyName)
      }
    }
  }

  isSiteAdminOrPortalAdmin = (): boolean => !!this.props.isPortalAdmin || !!this.props.isSiteAdmin
  isDisabled = () => {
    const { portalMode, isNew } = this.props
    const { portalData, retrievedPortals, companyData, retrievedCompanies } = this.state
    return portalMode
      ? !(isNew && portalMode) && (!portalData || !!retrievedPortals)
      : !isNew && (!companyData || !!retrievedCompanies)
  }
  isFormValid = () => {
    const {
      name, isNameValid, isNameAvailable,
      code, isCodeValid, isCodeAvailable,
      streetAddress1, isStreetAddress1Valid,
      city, isCityValid,
      state, isStateValid,
      zip, isZipValid,
      officePhone, isPhoneValid,
      fax,
      businessTypeId, isBusinessTypeValid,
      primaryIDType, isPrimaryIDTypeValid,
      isPortalFieldsValid,
      subscriptionPlanID,
      urlAvailable,
      contactPhone, contactFirstName, contactLastName,
      contactDerPhone, contactDerFirstName, 
      contactDerLastName, contactDerEmail,
      activeLabAccountsCount, labAcctRequired,
      companyData, portalData,
      isMROFileSizeValid
    } = this.state
    const { portalMode, isNew, isCompanyAdmin, isPortalAdmin, isSiteAdmin } = this.props
    const commonFormValid =
      !!streetAddress1 && isStreetAddress1Valid &&
      !!city && isCityValid &&
      !!state && isStateValid &&
      !!zip && isZipValid &&
      !!officePhone && isPhoneValid
    const disableCodeEdit = (!!companyData?.Code && companyData?.Code.length > 0) || (!isPortalAdmin && !isSiteAdmin)

    let valid = false
    if (isCompanyAdmin) { // edit company
      valid = !!name && isNameValid && isNameAvailable === true  &&
      !!contactFirstName && !!contactLastName && !!contactPhone && this.isPhoneValid(contactPhone) &&
      !!contactDerFirstName && !!contactDerLastName && !!contactDerPhone && this.isPhoneValid(contactDerPhone) &&
      !!primaryIDType && isPrimaryIDTypeValid &&
      (activeLabAccountsCount > 0 || !labAcctRequired) &&
      (!fax || this.isPhoneValid(fax))
    } else if (isPortalAdmin) {
      if (portalMode) { // edit portal
        valid = !!name && isNameValid &&
        subscriptionPlanID > 0 &&
        isMROFileSizeValid
      } else {
        if (isNew) { // create company
          valid = !!name && isNameValid && isNameAvailable === true &&
          (!code || (code.length > 0 && isCodeValid && isCodeAvailable === true)) &&
          !!contactFirstName && !!contactLastName && !!contactPhone && this.isPhoneValid(contactPhone) &&
          !!contactDerFirstName && !!contactDerLastName && !!contactDerPhone && this.isPhoneValid(contactDerPhone) &&
          this.isEmailValid(contactDerEmail) &&
          (portalData?.IsOnTrack || !!businessTypeId && isBusinessTypeValid)
        } else { // edit company
          valid = !!name && isNameValid && isNameAvailable === true &&
          (!code || disableCodeEdit || (code.length > 0 && isCodeValid && isCodeAvailable === true)) &&
          !!contactFirstName && !!contactLastName && !!contactPhone && this.isPhoneValid(contactPhone) &&
          !!contactDerFirstName && !!contactDerLastName && !!contactDerPhone && this.isPhoneValid(contactDerPhone) &&
          this.isEmailValid(contactDerEmail) &&
          (activeLabAccountsCount > 0 || !labAcctRequired)
        }
      }
    } else if (isSiteAdmin) {
      if (portalMode) {
        if (isNew) { // create portal
          valid = isPortalFieldsValid &&
          subscriptionPlanID > 0 &&
          urlAvailable === true &&
          isMROFileSizeValid
        } else { // edit portal
          valid = !!name && isNameValid &&
          subscriptionPlanID > 0 &&
          isMROFileSizeValid
        }
      } else {
        if (isNew) { // create company
          valid = !!name && isNameValid && isNameAvailable === true &&
          (!code || (code.length > 0 && isCodeValid && isCodeAvailable === true)) &&
          (portalData?.IsOnTrack || !!businessTypeId && isBusinessTypeValid)
        } else { // edit company
          valid = (activeLabAccountsCount > 0 || !labAcctRequired) &&
          (!code || disableCodeEdit || (code.length > 0 && isCodeValid && isCodeAvailable === true))
        }
      }
    }

    return commonFormValid && valid
  }
  isNameValid = (value: string) => this.isDisabled() || (this.props.isNew && this.props.portalMode || value.trim().length > 4)
  isStreetAddress1Valid = (value: string) => this.isDisabled() || !!value.length
  isCityValid = (value: string) => this.isDisabled() || !!value.length
  isStateValid = (value: string) => this.isDisabled() || !!value.length
  isZipValid = (value: string) => this.isDisabled() || (!!value.length && ValidZipCode(value))
  isPhoneValid = (value: string) => this.isDisabled() || (!!value.length && (value.replace(/\D/g, "").length === 10 || PhoneRegexp.test(value)))
  isBusinessTypeValid = (value?: number) => this.isDisabled() || !!value || !this.state.companyData?.IsOnTrack
  isPrimaryIDTypeValid = (value: number) => this.isDisabled() || !isCompanyAdmin || value > 0

  isUrlAllowed = (value: string) => !ProtectedSites.includes(value)
  isUrlMaxLengthValid = (value: string) => value.trim().length <= 56
  isUrlValid = (value: string) => value.trim().length > 0 && this.isUrlMaxLengthValid(value)
  isEmailValid = (value: string) => {
    if(!value){
      return true
    }
    return EmailRegexp.test(value)
  }
  prepareUrl = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "").toLowerCase()
  changeUrlStatePart = (url: string) => ({ url: url, urlValid: this.isUrlValid(url), urlAvailable: undefined, urlCheckMessage: undefined })

  isCompanyNameValid = (value: string) => {
    return this.isNameLengthValid(value) && this.isNameTextValid(value)
  }
  isNameTextValid = (value: string) => {
    const regexp = new RegExp(CompanyNameRegexp);
    return regexp.test(value)
  }
  isNameLengthValid = (value: string) => {
    const len = value.trim().length
    return len >= 3 && len <= 32
  }
  isCodeValid = (value: string) => /^[a-zA-Z0-9]*$/.test(value) && value.trim().length <= 10

  mapCompanyData = (data?: CompanyData[]): CompanyInformationData => {
    if (!data || data.length !== 1) {
      return { ...this.emptyData, name: this.state.name }
    }
    const info = data[0]
    return {
      name: info.Name,
      code: info.Code,
      streetAddress1: info.Address1 || "",
      streetAddress2: info.Address2 || "",
      city: info.City || "",
      state: this.mapState(info, this.state.states),
      zip: info.Zip || "",
      province: info.Province,
      country: info.Country,
      timeZoneID: info.TimeZoneID,
      webSiteURL: info.WebsiteURL,
      fax: info.Fax || "",
      officePhone: info.Phone || "",
      contactDerPhone: info.ContactDerPhone || "",
      contactDerEmail: info.ContactDerEmail || "",
      contactDerFirstName: info.ContactDerFirstName || "",
      contactDerLastName: info.ContactDerLastName || "",
      contactPhone: info.ContactPhone || "",
      contactFirstName: info.ContactFirstName || "",
      contactLastName: info.ContactLastName || "",
      businessTypeId: info.BusinessTypeID,
      portalAdminRoleID: this.state.roles ? this.mapRole(info, this.state.roles) || 0 : 0,
      primaryIDType: this.state.primaryIDTypes ? this.mapPrimaryIDType(info, this.state.primaryIDTypes) || 0 : 0,
      companyData: data[0],
      labAcctRequired: info.LabAcctRequired,
      inactive: info.Inactive,
      integrationType: info.IntegrationType ?? ""
    }
  }

  mapPortalData = (data?: PortalData[]): PortalInformationData => {
    if (!data || data.length !== 1) {
      return { ...this.emptyData }
    }
    const info = data[0]
    return {
      implementationID: info.ImplementationID,
      name: info.CompanyName || "",
      code: "",
      streetAddress1: info.Address1 || "",
      streetAddress2: info.Address2 || "",
      city: info.City || "",
      state: this.mapState(info, this.state.states),
      zip: info.Zip || "",
      officePhone: info.Phone || "",
      isTPA: info.ImplementationType === ImplementationType.TPA,
      subscriptionPlanID: info.SubscriptionPlanID,
      subscriptionPlan: this.state.subscriptionPlans ? this.mapSubscriptionPlan(info, this.state.subscriptionPlans) : undefined,
      implementationType: info.ImplementationType || ImplementationType.NonTPA,
      portalData: { ...data[0], IsOnTrack: location.host.includes("ontrack") },
      url: info.SubDomain || "",
      inactive: info.InActive,
      unmaskDonorID: info.UnmaskDonorID,
      NoLabAccount: info.NoLabAccount,
      generateResultExceptions: info.GenerateResultExceptions,
      hideAuthESS: info.HideAuthESS
    }
  }

  mapRole = (info: CompanyData, roles: Role[]): number | undefined => roles.find(item => item.RoleID === info.PortalAdditionalRoleID)?.RoleID
  mapPrimaryIDType = (info: CompanyData, types: PrimaryIDType[]): number | undefined => types.find(item => item.PrimaryIDType === info.PrimaryIDType)?.PrimaryTypeID
  mapState = (info: CompanyData | PortalData, states: State[]): string => states.find(item => item.StateProvinceTerritoryID === info.StateProvinceTerritoryID)?.Abbreviation || info.State || ""
  mapSubscriptionPlan = (info: PortalData, plans: SubscriptionPlan[]): SubscriptionPlan | undefined => plans.find(item => item.SubscriptionPlanID === info.SubscriptionPlanID)

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleAddressSelect = (address: Address | string | undefined) => {
    if (address) {
      if (typeof address === 'string') {
        this.setState({ streetAddress1: address, isStreetAddress1Valid: this.isStreetAddress1Valid(address) })
      } else if (address as Address) {
        const street = address.StreetLine || ""
        const state = address.State || ""
        const city = address.City || ""
        this.setState({
          streetAddress1: street, isStreetAddress1Valid: this.isStreetAddress1Valid(street),
          state: state, isStateValid: this.isStateValid(state),
          city: city, isCityValid: this.isCityValid(city)
        })
      }
    } else {
      this.setState({ streetAddress1: "", isStreetAddress1Valid: false })
    }
  }

  handlePortalNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.props.portalMode && this.isSiteAdminOrPortalAdmin()) {
      this.setState({ ...this.emptyData, name: e.target.value, isNameValid: this.isNameValid(e.target.value) })
    } else {
      this.setState({ name: e.target.value, isNameValid: this.isCompanyNameValid(e.target.value) })
    }
  }

  handleCompanyChange = (items: string[]) => {
    const selectedCompany = items[0]
    if (selectedCompany) {
      this.setState({ selectedCompany: selectedCompany })
      this.companyName$.next(selectedCompany)
    }
    else {
      this.setState({ selectedCompany: undefined })
    }
  }

  handleCompanyNameTyping = (company: string) => {
    if (company == "") {
      this.setState({ ...this.mapCompanyData(), selectedCompany: undefined, companies: [], allCompanies: [] })
    }
    else {
      this.setState({ name: company })
      this.searchCompany$.next(company)
    }
  }

  handlePortalNameTyping = (name: string) => {
    if (name == "") {
      this.setState({ ...this.mapPortalData(), retrievedPortalPredictions: [] })
    }
    else {
      this.searchImplementation$.next(name)
    }
  }

  handleRetrievedCompanyAddressSelect = () => this.setState({
    retrievedCompanies: undefined,
    ...this.mapCompanyData(this.state.companyData ? [this.state.companyData] : undefined)
  })

  handleRetrievedPortalAddressSelect = () => this.setState({
    retrievedPortals: undefined,
    ...this.mapPortalData(this.state.portalData ? [this.state.portalData] : undefined)
  })

  performInactivate = () => {
    this.setState({ inactivating: true, showInactivateConfirmation: false })
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
    this.inactivateSubscription = (
      this.props.portalMode
        ? PortalInactivateObservable(this.state.portalData?.ImplementationID || 0)
        : CompanyInactivate(this.state.companyData?.ImplementationID || 0, this.state.companyData?.AccountID || 0)
    ).subscribe(
      data => {
        this.setState({ inactivating: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { inactivating: false })
    )
  }

  performActivate = () => {
    this.setState({ activating: true })
    this.inactivateSubscription && this.inactivateSubscription.unsubscribe()
    this.inactivateSubscription = (
      this.props.portalMode
        ? PortalActivationObservable(this.state.portalData?.ImplementationID || 0)
        : CompanyActivationObservable(this.state.companyData?.ImplementationID || 0, this.state.companyData?.AccountID || 0)
    ).subscribe(
      data => {
        this.setState({ activating: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { activating: false })
    )
  }

  handleRetrievedObjectChoose = (selected: AddressData) => {
    if (this.props.portalMode) {
      this.setState({ portalData: this.state.retrievedPortals?.find(item => item.ImplementationID.toString() === selected.ID) })
    } else {
      this.setState({ companyData: this.state.retrievedCompanies?.find(item => item.AccountID.toString() === selected.ID) })
    }
  }

  handleCancel = () => {
    if (this.implementationName && this.props.portalMode) {
      (this.props as RouteComponentProps).history?.push(PortalAdministrationRoute)
    } else if (this.implementationName && !this.props.portalMode && this.props.isNew) {
      (this.props as RouteComponentProps).history?.push(PortalDetailsRoute(this.implementationName))
    } else if (!this.implementationName && !this.props.portalMode && this.props.isNew && !this.state.saved) {
      (this.props as RouteComponentProps).history?.push(MyOrganizationRoute)
    } else if (!this.implementationName && !this.props.portalMode && this.props.isNew && this.state.saved) {
      (this.props as RouteComponentProps).history?.push(CompanyDetailsRoute(this.state.name))
    } else {
      (this.props as RouteComponentProps).history?.push(Home)
    }
  }

  handleUpdate = () => {
    this.props.portalMode && !this.props.isNew && this.updatePortal()
    this.props.portalMode && this.props.isNew && this.createPortal()
    !this.props.portalMode && this.updateCompany()
  }

  handlePortalFieldsValidation = (data: PortalAdminData, valid: boolean) => {
    this.setState({ portalAdminData: data, isPortalFieldsValid: valid })
  }

  updateCompany = () => {
    const { portalData, companyData, name, code, streetAddress1, streetAddress2, city, state, zip, officePhone, businessTypeId, roles, portalAdminRoleID, primaryIDType,
      primaryIDTypes, contactDerPhone, contactDerFirstName, contactDerLastName, contactDerEmail, contactPhone, contactFirstName, contactLastName,
      province, country, timeZoneID, webSiteURL, fax, labAcctRequired, integrationType } = this.state
    const { isPortalAdmin, isCompanyAdmin, isSiteAdmin, isNew } = this.props;

    if (!companyData && !isNew) {
      return
    }
    const codeValue = !code || code.length === 0 ? undefined : code.trim()

    const updateData: CompanyEditData = {
      AccountID: companyData?.AccountID || 0,
      Name: name.trim(),
      Code: codeValue,
      Address1: streetAddress1,
      Address2: streetAddress2,
      City: city,
      State: state,
      Zip: zip,
      Phone: officePhone.replace(/-/g, ""),
      BusinessTypeID: businessTypeId!,
      ImplementationID: companyData?.ImplementationID || portalData?.ImplementationID || this.state.implementationID || 0,
      IsRandomization: !!companyData && companyData.IsRandomization,
      Regulated: !!companyData && companyData.Regulated,
      NonRegulated: !!companyData && companyData.NonRegulated,
      PortalAdditionalRoleID: roles?.find(item => item.RoleID === portalAdminRoleID)?.RoleID || "",
      PrimaryTypeID: primaryIDTypes?.find(item => item.PrimaryTypeID === primaryIDType)?.PrimaryIDType || "",
      IsOnTrack: isNew ? (this.implementationName ? !!portalData?.IsOnTrack : IsOnTrack()) : companyData?.IsOnTrack || false,
      ContactDerFirstName: contactDerFirstName,
      ContactDerLastName: contactDerLastName,
      ContactDerPhone: contactDerPhone.replace(/-/g, ""),
      ContactDerEmail: contactDerEmail,
      ContactFirstName: contactFirstName,
      ContactLastName: contactLastName,
      ContactPhone: contactPhone.replace(/-/g, ""),
      UpdateAdditionalFields: this.showAdditionalFields(),
      Province: province ? province : undefined,
      Country: province ? country : undefined,
      TimeZoneID: timeZoneID,
      WebsiteURL: webSiteURL ? webSiteURL : undefined,
      Fax: fax ? fax : undefined,
      LabAcctRequired: isNew ? false : labAcctRequired,
      IntegrationType: integrationType
    }

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    let observ = isNew ? CompanyCreateObservable : CompanyUpdateObservable
    this.saveSubscription = observ(updateData).subscribe(
      (data) => {
        this.setState({ saving: false, saved: true })
        toast.success(data)
        this.props.reload()
        if (isNew) {
          this.handleCancel()
        }
        else {
          this.companyName$.next(name.trim())
        }
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  createPortal = () => {
    const { streetAddress1, streetAddress2, city, state, zip, officePhone, subscriptionPlan, isTPA, portalAdminData, url, implementationType, unmaskDonorID, NoLabAccount, generateResultExceptions, hideAuthESS } = this.state
    if (!portalAdminData) {
      return
    }
    const updateData: PortalInviteData = {
      CompanyName: portalAdminData.Name,
      EmailAddress: portalAdminData.Email,
      PhoneNumber: portalAdminData.Phone.replace(/-/g, ""),
      FirstName: portalAdminData.FirstName,
      LastName: portalAdminData.LastName,
      RoleID: portalAdminData.RoleID || 0,
      SubscriptionPlanID: subscriptionPlan?.SubscriptionPlanID || 0,
      IsGOVTPortal: (subscriptionPlan?.Title || "") === "Government Program",
      Link: `${url}.${SiteDomain}`,
      ModifiedBy: this.props?.userName || "",
      IsExistingUser: false,
      ImplementationID: 0,
      Address1: streetAddress1,
      Address2: streetAddress2,
      City: city,
      State: state,
      Zip: zip,
      OfficePhone: officePhone.replace(/-/g, ""),
      ImplementationType: implementationType || ImplementationType.NonTPA,
      UnmaskDonorID: unmaskDonorID,
      NoLabAccount: NoLabAccount,
      GenerateResultExceptions: generateResultExceptions,
      HideAuthESS: hideAuthESS,
    };

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = PortalInvitation(updateData).subscribe(
      (result) => {
        (this.props as RouteComponentProps).history?.push(PortalAdministrationRoute)
        toast.success(result)
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  updatePortal = () => {
    const { portalData, name, streetAddress1, streetAddress2, city, state, zip, officePhone, subscriptionPlan, isTPA, mroFileContent, implementationType, unmaskDonorID, NoLabAccount, generateResultExceptions, hideAuthESS } = this.state
    if (!portalData) {
      return
    }
    const updateData: PortalEditData = {
      CompanyName: name.trim(),
      Address1: streetAddress1,
      Address2: streetAddress2,
      City: city,
      State: state,
      Zip: zip,
      ImplementationName: name.trim(),
      Phone: officePhone.replace(/-/g, ""),
      SubscriptionPlanID: subscriptionPlan?.SubscriptionPlanID || portalData.SubscriptionPlanID,
      SubscriptionPlan: subscriptionPlan?.Title || portalData.SubscriptionPlan || "",
      ImplementationID: portalData.ImplementationID,
      ImplementationType: implementationType || ImplementationType.NonTPA,
      IsOnTrack: portalData.IsOnTrack,
      MROLogoFile: mroFileContent,
      UnmaskDonorID: unmaskDonorID,
      NoLabAccount: NoLabAccount,
      GenerateResultExceptions: generateResultExceptions,
      HideAuthESS: hideAuthESS,
    }

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = PortalUpdate(updateData).subscribe(
      (data) => {
        this.setState({ saving: false, mroFileName: "" })
        this.updateSeletectPortal(updateData)
        toast.success(data)
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  updateSeletectPortal = (updateData: PortalEditData) =>{
    if(!updateData || !this.props.portal){
      return
    }
    if(this.props.portal.ImplementationID !== updateData.ImplementationID){
      return
    }
    this.props.portal.ImplementationName = updateData.ImplementationName
    this.props.portal.UnmaskDonorID = updateData.UnmaskDonorID
    this.props.portal.NoLabAccount = updateData.NoLabAccount
  }

  showAdditionalFields = (): boolean => !this.props.portalMode && !this.props.isSiteAdmin

  loadLabAccounts() {
    const { companyData } = this.state
    if (!companyData) {
      return
    }
    this.setState({ labAccountsLoading: true, activeLabAccountsCount: 0 })
    this.loadLabAccountsSubscription && this.loadLabAccountsSubscription.unsubscribe()
    this.loadLabAccountsSubscription = LabAccountListGet(companyData.ImplementationID, companyData.AccountID, false).subscribe(
      data => this.setState({ activeLabAccountsCount: data.ActiveLabAccount.length, labAccountsLoading: false }),
      error => this.handleAPIError(error, { labAccountsLoading: false })
    )
  }

  handleLabAccountsSelectionModalClose = () => {
    this.setState({ showLabAccountSelection: false })
    if (!this.props.portalMode) {
      this.loadLabAccounts()
    }
  }

  handleLabAccountsNotRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ labAcctRequired: !e.target.checked })

  handleNameChange = (name: string) => {
    const url = this.prepareUrl(name)
    this.setState({
      ...this.changeUrlStatePart(url)
    })
  }

  handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || ""
    this.setState({
      name: value,
      isNameValid: this.isCompanyNameValid(value),
      isNameLengthValid: this.isNameLengthValid(value),
      isNameTextValid: this.isNameTextValid(value),
      isNameAvailable: undefined
    })
  }
  handleCompanyNameBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = (e.target.value || "").trim()
    if(this.state.name !== value){
      this.setState({name: value})
    }

    this.checkName$.next({ value: value, implementationID: this.state.portalData?.ImplementationID })
  }
  handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "")
    this.setState({ code: value, isCodeValid: this.isCodeValid(value), isCodeAvailable: undefined })
  }

  handleCompanyCodeBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.checkCode$.next(e.target.value)
  }

  handleAddCompany = () => {
    if (this.implementationName) {
      (this.props as RouteComponentProps).history?.push(AddCompanyForPortalRoute(this.implementationName))
    } else {
      (this.props as RouteComponentProps).history?.push(AddNewCompanyRoute)
    }
  }

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (!file) {
      this.setState({ mroFileName: "", mroFileContent: undefined, isMROFileSizeValid: true })
      return
    }

    const isFileSizeValid = file.size < MaxImageFileSize
    this.setState({ mroFileName: file.name, isMROFileSizeValid: isFileSizeValid })
    if (isFileSizeValid) {
      const ext = file.name.substring(file.name.lastIndexOf('.') + 1)
      if (ext && ["bmp"].includes(ext.toLowerCase())) {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf('base64') + 7
          const fileContent = result.slice(stringLength, result.length)
          if (fileContent) {
            self.setState({ mroFileContent: fileContent })
          } else {
            toast.error("Image file is empty.")
          }
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("Please upload only .bmp type of image files.")
      }
    }
  }

  handleCompaniesExport = () => {
    this.setState({ loadingCsv: true })
    CompaniesDetailsExportCsv().subscribe(
      data => {
        this.export(data)
        this.setState({ loadingCsv: false })
      },
      error => this.handleAPIError(error, { loadingCsv: false })
    )
  }

  handleHideAuthChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    this.setState({ hideAuthESS: e.target.checked })
  }

  handleUnmackDonorIDChange = (checked: boolean) => 

  {
    this.setState({ unmaskDonorID: checked ? 1 : 0 })
  }

  handleGenerateResultExceptionsChange = (checked: boolean) =>
  {
    this.setState({ generateResultExceptions: checked })
  }

  handleNoLabAccountChange = (checked: boolean) => 
  {
    this.setState({ NoLabAccount: checked ? 1 : 0 })
  }

  export(csv: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("CompaniesDetails", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  handleIntegrationTypeChange = (value: string) => this.setState({ integrationType:value })

  render() {
    const { companyData, retrievedCompanies, portalData, retrievedPortals,
      name, code, streetAddress1, streetAddress2, city, state, zip, province, country, timeZoneID, webSiteURL, fax, officePhone, contactPhone, contactFirstName, contactLastName,
      contactDerPhone, contactDerFirstName, contactDerLastName, contactDerEmail,
      isTPA, subscriptionPlan, subscriptionPlanID, implementationType, implementationID,
      businessTypeId, businessTypesLoading, businessTypes,
      portalAdminRoleID, roles, rolesLoading,
      primaryIDType, primaryIDTypes, primaryIDTypesLoading,
      states, statesLoading, activating,
      loading, inactivating, saving,
      timeZones, timeZonesLoading,
      labAccountsLoading, activeLabAccountsCount, labAcctRequired,
      showOrderSecurity, showOrderSecurityOverride, showLabAccountSelection, showSubscriptions, showManageImages, showCompanyImport, showLocation,
      showAdditionalTesting, isNameValid, isNameChecking, isNameAvailable, isNameLengthValid, isNameTextValid, showLocationImport,
      isCodeValid, isCodeChecking, isCodeAvailable,
      isStreetAddress1Valid, isCityValid, isStateValid, isZipValid, isPhoneValid, isBusinessTypeValid, isPrimaryIDTypeValid,
      url, urlChecking, urlAvailable, urlValid, urlCheckMessage, mroFileName, isMROFileSizeValid, loadingCsv, showDevices, unmaskDonorID,
      NoLabAccount, integrationType, generateResultExceptions, hideAuthESS
    } = this.state

    const disableCheckUrl = !!url && (url.length === 0 || !urlValid || !!portalData?.ImplementationID || !this.isUrlAllowed(url))
    const { portalMode, isCompanyAdmin, isPortalAdmin, isSiteAdmin, userAccess, isNew } = this.props

    const disabled = this.isDisabled()
    const allowAdditionalRole = !disabled && !!companyData?.AllowAdditionalRole
    const validContactPhone = disabled || this.isPhoneValid(contactPhone)
    const validContactFirstName = disabled || !!contactFirstName.length
    const validContactLastName = disabled || !!contactLastName.length
    const validContactDerPhone = disabled || this.isPhoneValid(contactDerPhone)
    const validContactDerEmail = disabled || this.isEmailValid(contactDerEmail)
    const validContactDerFirstName = disabled || !!contactDerFirstName.length
    const validContactDerLastName = disabled || !!contactDerLastName.length
    const labAccountsCountValid = portalMode || activeLabAccountsCount > 0 || !labAcctRequired
    const canImport = ((portalMode || (isSiteAdmin && this.implementationName && !isNew)) && userAccess?.canCreateClient)
    const canImportLocation = !portalMode && (isPortalAdmin || isCompanyAdmin || isSiteAdmin)
    const isSiteAdminOrPortalAdmin = this.isSiteAdminOrPortalAdmin()
    const showAdditionalFields = this.showAdditionalFields()
    const showActivateButton =  (portalMode && portalData?.InActive) || companyData?.Inactive
    const disableCodeEdit = (!!companyData?.Code && companyData?.Code.length > 0) || (!isPortalAdmin && !isSiteAdmin)

    const isIntl11 = !!officePhone.length && (officePhone.replace(/\D/g, "").length === 11)
    const isFaxIntl11 = !!fax.length && (fax.replace(/\D/g, "").length === 11)

    return (<AppPageWrapper showNav>
      {showOrderSecurity && <OrderSecurity override companyName={name} cancelText="Cancel" submitText="Submit" cancelVariant="secondary"
        onPrev={() => this.setState({ showOrderSecurity: false })} onNext={() => this.setState({ showOrderSecurity: false })} />}
      {(isNew && portalMode || companyData || portalData) &&
        <LabAccountSelection
          portalMode={portalMode}
          name={portalMode
            ? portalData?.CompanyName || ""
            : companyData?.Name || ""}
          implementationID={portalMode
            ? portalData?.ImplementationID || 0
            : companyData?.ImplementationID || 0}
          accountID={portalMode ? 0 : companyData?.AccountID || 0}
          show={showLabAccountSelection}
          onClose={this.handleLabAccountsSelectionModalClose} />}
        {showDevices && <DeviceSelection
          portalMode={portalMode}
          implementationID={portalMode
            ? portalData?.ImplementationID || 0
            : companyData?.ImplementationID || 0}
          accountID={portalMode ? 0 : companyData?.AccountID || 0}
          onClose={() => this.setState({ showDevices: false })} />}
      {!showLocation && <Modal.Dialog size="xl">
        <Modal.Header>
          {portalMode && <Modal.Title className="text-uppercase">EDIT MY ORGANIZATION</Modal.Title>}
          {!portalMode && <Modal.Title className="text-uppercase">Edit Company Information</Modal.Title>}
        </Modal.Header>
        <Modal.Body>
          <Form>
            {isNew && portalMode && <PortalFields isValid={(data, valid) => this.handlePortalFieldsValidation(data, valid)} onNameChange={this.handleNameChange} />}
            {portalMode && !isNew && <Form.Row className="form-group required">
              <Form.Label column lg={4} htmlFor="companyName">Organization Name</Form.Label>
              <Col lg={8}>
                {(!isSiteAdmin || this.implementationName) && <>
                  {loading && <div className="py-2"><Spinner animation="border" size="sm" variant="secondary" /></div>}
                  {!loading && <Form.Control id="companyName" type="text" value={name} isInvalid={!isNameValid} disabled={this.state.loading} autoComplete="off" onChange={this.handlePortalNameChange} />}
                </>}
                {isSiteAdmin && !this.implementationName && <AsyncTypeahead id="implementation"
                  ref={this.typeahead}
                  paginate={false}
                  useCache={false}
                  isLoading={this.state.loading}
                  onSearch={() => { }}
                  onInputChange={this.handlePortalNameTyping}
                  onChange={selected => this.implementationName$.next(selected[0] || "")}
                  options={this.state.retrievedPortalPredictions || []}
                  selected={this.state.portalData ? [this.state.portalData.CompanyName || ""] : []}
                  minLength={0}
                  emptyLabel="No Portals Found"
                  placeholder=""
                  inputProps={{ autoComplete: "chrome-off" }}>
                  {!this.state.loading &&
                    <Button variant="toggle" onClick={() => { this.typeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                </AsyncTypeahead>}
              </Col>
            </Form.Row>}
            {!portalMode &&
            <Form.Row className="form-group required">
              <Col lg={4} className="col-form-label">
                {!isNew && <Form.Label className="mb-0" htmlFor="companyName">Company {companyData?.AccountID ? `(Account ID: ${companyData?.AccountID})` : ""}</Form.Label>}
                {isNew && <Form.Label className="mb-0" htmlFor="companyName">Company's Name</Form.Label>}
              </Col>
              <Col lg={8}>
                {!isNew &&
                <InputGroup>
                  {!isSiteAdminOrPortalAdmin && loading && <div className="py-2"><Spinner animation="border" size="sm" variant="secondary" /></div>}
                  {isSiteAdminOrPortalAdmin &&
                  <AsyncTypeahead id="company-select"
                    ref={this.typeahead}
                    paginate={false}
                    useCache={false}
                    isLoading={this.state.loading}
                    onSearch={() => { }}
                    onInputChange={this.handleCompanyNameTyping}
                    onChange={this.handleCompanyChange}
                    options={this.state.companies || []}
                    selected={this.state.selectedCompany ? [this.state.selectedCompany || ""] : []}
                    minLength={0}
                    emptyLabel="No Companies Found"
                    placeholder=""
                    inputProps={{ autoComplete: "chrome-off" }}>
                    {!this.state.loading &&
                      <Button variant="toggle" onClick={() => { this.typeahead.current?.toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </AsyncTypeahead>}
                </InputGroup>}
                {!loading && (!isSiteAdminOrPortalAdmin || isNew ) && <>
                  <Form.Control id="name"
                    type="text"
                    value={name}
                    onChange={this.handleCompanyNameChange}
                    onBlur={this.handleCompanyNameBlur}
                    isInvalid={!isNameValid || !isNameAvailable}
                    disabled={isNameChecking || !isSiteAdminOrPortalAdmin} />
                  {!isNameChecking && !isNameValid && !isNameLengthValid && isNameTextValid && <Form.Control.Feedback type="invalid" className="d-block">Company Name must be of 3 to 32 characters.</Form.Control.Feedback>}
                  {!isNameChecking && !isNameValid && isNameLengthValid && !isNameTextValid  && <Form.Control.Feedback type="invalid" className="d-block">Company name cannot contain any special characters except apostrophe, dash, comma or dot  [ ' or - or , or . ]</Form.Control.Feedback>}
                  {!isNameChecking && !isNameValid && !isNameLengthValid && !isNameTextValid  && <Form.Control.Feedback type="invalid" className="d-block">Company Name must be of 3 to 32 characters. Company name cannot contain any special characters except apostrophe, dash, comma or dot  [ ' or - or , or . ]</Form.Control.Feedback>}
                  {isNew && !isNameChecking && isNameAvailable && <Form.Control.Feedback type="valid" className="d-block">Company name is available</Form.Control.Feedback>}
                  {!isNameChecking&& isNameAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">Company name is already in use</Form.Control.Feedback>}
                </>}
              </Col>
            </Form.Row>}
            {isSiteAdmin && portalMode &&
              <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="companyCode">Client Code/ID</Form.Label>
                <Col lg={8}>
                  {!isNew && <Form.Control id="codeCode" type="text" disabled={disabled} value={code} onChange={e => this.setState({ code: e.target.value })} />}
                  {isNew && <>
                    <Form.Control id="codeCode"
                      type="text"
                      value={code}
                      onChange={this.handleCodeChange}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => this.checkCode$.next(e.target.value)}
                      isInvalid={!isCodeValid}
                      disabled={disabled} />
                    {!isCodeChecking && !isCodeValid && <Form.Control.Feedback type="invalid" className="d-block">Please enter a valid client code/ID.</Form.Control.Feedback>}
                    {!isCodeChecking && isCodeAvailable && <Form.Control.Feedback type="valid" className="d-block">Client code/ID is available.</Form.Control.Feedback>}
                    {!isCodeChecking && isCodeAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">The client code/ID is already in use, please enter a unique value.</Form.Control.Feedback>}
                  </>}
                </Col>
              </Form.Row>}
            {!portalMode &&
              <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="companyCode">Company Code</Form.Label>
                <Col lg={8}>
                  <Form.Control id="companyCode" type="text" className={disabled || disableCodeEdit ? '' : 'bg-white'} onBlur={this.handleCompanyCodeBlur} value={code} disabled={disabled || disableCodeEdit} onChange={this.handleCodeChange} />
                  {!isCodeChecking && !isCodeValid && <Form.Control.Feedback type="invalid" className="d-block">Please enter a valid company Code.</Form.Control.Feedback>}
                  {!isCodeChecking && isCodeAvailable && <Form.Control.Feedback type="valid" className="d-block">Company Code is available.</Form.Control.Feedback>}
                  {!isCodeChecking && isCodeAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">The company Code is already in use, please enter a unique value.</Form.Control.Feedback>}
                </Col>
              </Form.Row>}
            <Form.Row className="form-group required">
              <Form.Label column lg={4} htmlFor="streetAddress1">Street Address 1</Form.Label>
              <Col lg={8}>
                <AddressInput onAddressChange={this.handleAddressSelect}
                  isInvalid={!isStreetAddress1Valid}
                  disabled={disabled}
                  selected={{ City: city, State: state, StreetLine: streetAddress1, Text: streetAddress1 }} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={4} htmlFor="streetAddress2">Street Address 2</Form.Label>
              <Col lg={8}>
                <Form.Control id="streetAddress2" type="text"
                  autoComplete="new-password"
                  value={streetAddress2}
                  disabled={disabled}
                  onChange={e => this.setState({ streetAddress2: e.target.value })} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column lg={4} htmlFor="city">City</Form.Label>
              <Col lg={8}>
                <Form.Control id="city" type="text"
                  autoComplete="new-password"
                  value={city}
                  onChange={e => this.setState({ city: e.target.value, isCityValid: this.isCityValid(e.target.value) })}
                  disabled={disabled}
                  isInvalid={!isCityValid} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column lg={4} htmlFor="state">State</Form.Label>
              <Col sm={5}>
                <StateSelect selectedCode={state}
                  states={states}
                  onChange={selected => this.setState({ state: selected?.Abbreviation || "", isStateValid: this.isStateValid(selected?.Abbreviation) })}
                  isDisabled={disabled || statesLoading}
                  isInvalid={!isStateValid} />
              </Col>
              <Form.Label column sm={1} htmlFor="zip">ZIP</Form.Label>
              <Col sm={2}>
                <Form.Control id="zip" type="text"
                  autoComplete="new-password"
                  onChange={e => this.setState({ zip: e.target.value, isZipValid: this.isZipValid(e.target.value) })}
                  value={zip}
                  disabled={disabled}
                  isInvalid={!isZipValid} />
              </Col>
            </Form.Row>
            {showAdditionalFields && <Form.Row className="form-group">
              <Form.Label column lg={4} htmlFor="province">Province</Form.Label>
              <Col lg={8}>
                <Form.Control id="province" type="text"
                  autoComplete="new-password"
                  value={province || ""}
                  onChange={e => this.setState({ province: e.target.value })}
                  disabled={disabled} />
              </Col>
            </Form.Row>}
            {showAdditionalFields && <Form.Row className="form-group">
              <Form.Label column lg={4} htmlFor="country">Country</Form.Label>
              <Col lg={8}>
                <Form.Control id="province" type="text"
                  autoComplete="new-password"
                  value={country || ""}
                  onChange={e => this.setState({ country: e.target.value })}
                  disabled={disabled} />
              </Col>
            </Form.Row>}
            <Form.Row className="form-group required">
              <Form.Label column lg={4} htmlFor="officePhone">Office Phone #</Form.Label>
              <Col lg={8}>
                <PhoneNumberMaskedInput
                  autoComplete="new-password"
                  value={officePhone}
                  onChange={(value: string) => this.setState({ officePhone: value, isPhoneValid: this.isPhoneValid(value) })}
                  disabled={disabled}
                  isInvalid={!isPhoneValid}
                  intl11={isIntl11} />
              </Col>
            </Form.Row>
            {portalMode && <>
              {!portalData?.IsOnTrack && <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="tpa">
                  Are you a Third-Party Administrator?
                  <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={
                    <Popover id="tpa" className="p-2">
                      Third-Party Administrator (TPA) or Consortium/Third-Party Administrators (C/TPAs)
                      manage all, or part, of an employer's drug and alcohol testing program. This
                      may include maintaining testing records and performing tasks to implement the
                      testing program.
                    </Popover>}>
                    <FaInfoCircle className="info-icon" />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Check inline id="tpa-yes" name="tpa" type="radio" label="Yes" className="abc-radio mx-2" checked={isTPA === true} onChange={() => this.setState({ isTPA: true })} />
                <Form.Check inline id="tpa-no" name="tpa" type="radio" label="No" className="abc-radio mx-2" checked={isTPA === false} onChange={() => this.setState({ isTPA: false })} />
              </Form.Row>}
              {isSiteAdmin && <>
                <Form.Row className="form-group required">
                  <Form.Label column lg={4} htmlFor="subscription">Subscription</Form.Label>
                  <Col lg={8}>
                    <InputGroup>
                      <Form.Control type="text" disabled defaultValue={subscriptionPlan?.Title || ""} className="bg-white" />
                      <InputGroup.Append>
                        <Button variant="secondary" onClick={() => this.setState({ showSubscriptions: true })} disabled={disabled}>Change Subscription</Button>
                      </InputGroup.Append>
                    </InputGroup>
                  </Col>
                </Form.Row>
                {(isNew || !!implementationID) &&<Form.Row className="form-group required">
                  <Form.Label column lg={4} htmlFor="type">Implementation Type</Form.Label>
                  <Col lg={8}>
                    <Form.Control as="select" type="text" size="sm" defaultValue={ this.state.implementationType }
                      disabled={ isNew }
                      onChange={ (e) => this.setState({ implementationType: e.target.value }) }>
                      {ImplementationTypes.map((type) => <option key={type} value={ImplementationType[type]} title={ImplementationType[type]}>{ImplementationType[type]}</option> )}
                    </Form.Control>
                  </Col>
                </Form.Row>}
              </>
              }
              {portalMode && <>
                <Form.Row className="mb-1 pb-1">
                  <Form.Label column lg={4} htmlFor="url">URL of Portal Site</Form.Label>
                  <Col lg={8}>
                    <InputGroup>
                      <Form.Control type="text" className="text-right bg-white" id="url" value={url} isInvalid={!urlValid} readOnly={true} />
                      <InputGroup.Append><InputGroup.Text>.{SiteDomain}</InputGroup.Text></InputGroup.Append>
                    </InputGroup>
                  </Col>
                </Form.Row>
                <Form.Row>
                  <Col sm={12} className="text-right">
                    {!urlChecking && urlAvailable && <Form.Control.Feedback type="valid" className="d-inline">{urlCheckMessage}</Form.Control.Feedback>}
                    {!urlChecking && urlAvailable === false && <Form.Control.Feedback type="invalid" className="d-inline">{urlCheckMessage}</Form.Control.Feedback>}
                    {!this.isUrlAllowed(url) && <Form.Control.Feedback type="invalid" className="d-inline">Subdomain is protected</Form.Control.Feedback>}
                    {!this.isUrlMaxLengthValid(url) && <Form.Control.Feedback type="invalid" className="d-inline">URL length will be up to 56 character</Form.Control.Feedback>}
                    <ActionButton variant="secondary" className="ml-2" onClick={() => { this.checkUrl$.next(url)} } disabled={disableCheckUrl} spin={urlChecking} disableOnSpin>Check URL</ActionButton>
                  </Col>
                </Form.Row>
              </>}
            </>}
            {!portalMode && <>
              {showAdditionalFields && <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="officeFax">Office Fax #</Form.Label>
                <Col lg={8}>
                  <PhoneNumberMaskedInput
                    autoComplete="new-password"
                    value={fax}
                    onChange={(value: string) => this.setState({ fax: value })}
                    disabled={disabled}
                    isInvalid={(!disabled && (!!fax && !this.isPhoneValid(fax)))}
                    intl11={isFaxIntl11} />
                </Col>
              </Form.Row>}
              {!portalData?.IsOnTrack && !companyData?.IsOnTrack && isNew && <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="businessType">
                  Business Type
                  <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={
                    <Popover id="businessTypePopover">
                      <ListGroup>
                        <ListGroup.Item>An employer is a person or organization that employs people.</ListGroup.Item>
                        <ListGroup.Item>A Third-Party Administrator (TPA) or Consortium/Third-Party Administrators (C/TPAs) manages all, or part, of an employer's drug and alcohol testing program.</ListGroup.Item>
                        <ListGroup.Item>An occupational health clinic collects specimens and/or performs required occupational health services.</ListGroup.Item>
                        <ListGroup.Item>A laboratory receives, analyzes, and reports laboratory-confirmed results.</ListGroup.Item>
                        <ListGroup.Item>A Medical Review Officer (MRO) is a licensed physician trained and certified to review lab results and to validate whether a test is positive.</ListGroup.Item>
                      </ListGroup>
                    </Popover>}>
                    <FaInfoCircle className="info-icon" />
                  </OverlayTrigger>
                </Form.Label>
                <Col lg={8}>
                  <Form.Control as="select" id="businessType" type="text"
                    disabled={businessTypesLoading || disabled}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const businessTypeID = !!e.target.value ? Number(e.target.value) : undefined
                      this.setState({ businessTypeId: businessTypeID, isBusinessTypeValid: this.isBusinessTypeValid(businessTypeID) })
                    }}
                    isInvalid={!isBusinessTypeValid}
                    value={businessTypeId || "0"}>
                    <option value="">Select</option>
                    {businessTypes.map((item: BusinessType, key: number) => <option key={key} value={item.BusinessTypeID}>{item.Description}</option>)}
                  </Form.Control>
                </Col>
              </Form.Row> }
              {showAdditionalFields && <>
                <Form.Row className="form-group">
                  <Form.Label column lg={4}>Time Zone</Form.Label>
                  <Col lg={8}>
                    <Form.Control id="timeZones" as="select"
                      disabled={timeZonesLoading || disabled}
                      value={timeZoneID}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { this.setState({ timeZoneID: Number(e.target.value) })}}>
                        <option value={0} key={0}></option>
                        {timeZones?.map(tz => <option value={tz.TimeZoneID} key={tz.TimeZoneID}>{tz.Abbreviation}</option>)}
                    </Form.Control>
                  </Col>
                </Form.Row>
                <Form.Row className="form-group required">
                  <Form.Label column lg={4} htmlFor="contactFirstName">Company Contact First\Last Names</Form.Label>
                    <Col lg={4}>
                      <Form.Control id="contactFirstName" type="text"
                        autoComplete="new-password"
                        value={contactFirstName}
                        onChange={e => this.setState({ contactFirstName: e.target.value })}
                        isInvalid={!validContactFirstName}
                        disabled={disabled} />
                    </Col>
                    <Col lg={4}>
                      <Form.Control id="contactLastName" type="text"
                        autoComplete="new-password"
                        value={contactLastName}
                        onChange={e => this.setState({ contactLastName: e.target.value })}
                        isInvalid={!validContactLastName}
                        disabled={disabled} />
                    </Col>
                </Form.Row>
                <Form.Row className="form-group required">
                  <Form.Label column lg={4} htmlFor="contactPhone">Company Contact Phone #</Form.Label>
                    <Col lg={8}>
                      <PhoneNumberMaskedInput
                        autoComplete="new-password"
                        value={contactPhone}
                        onChange={(value: string) => this.setState({ contactPhone: value })}
                        disabled={disabled}
                        isInvalid={!validContactPhone} />
                    </Col>
                  </Form.Row>
                  <Form.Row className="form-group">
                    <Form.Label column lg={4} htmlFor="website">Website</Form.Label>
                    <Col lg={8}>
                      <Form.Control id="website" type="text"
                        autoComplete="new-password"
                        value={webSiteURL || ""}
                        onChange={e => this.setState({ webSiteURL: e.target.value })}
                        disabled={disabled} />
                    </Col>
                  </Form.Row></>}
                  {isCompanyAdmin && <Form.Row className="form-group">
                    <Form.Label column lg={4} htmlFor="businessType">Control who can view Orders placed by other users</Form.Label>
                    <Col lg={8}>
                      <Button variant="primary" onClick={() => this.setState({ showOrderSecurity: true })} disabled={disabled}>User Role</Button>
                      <Button variant="primary" disabled={disabled} onClick={() => this.setState({ showOrderSecurityOverride: true })} className="ml-2">Details to Person</Button>
                    </Col>
                  </Form.Row>}
            </>}
            {!isNew &&
            <Form.Row className="form-group">
              <Form.Label column lg={4} className="required">Lab Accounts</Form.Label>
              <Col>
                <ActionButton variant="secondary" onClick={() => this.setState({ showLabAccountSelection: true })} disabled={disabled} spin={labAccountsLoading} disableOnSpin={false}>Lab Account Details</ActionButton>
                {!portalMode && companyData && !labAccountsCountValid && !labAccountsLoading && <Form.Control.Feedback type="invalid" className="d-block">Lab Accounts required</Form.Control.Feedback>}
                {isSiteAdmin && !portalMode && companyData?.IsOnTrack && <ActionButton variant="secondary" className="ml-2" onClick={() => this.setState({ showDevices: true })} disabled={disabled}>Devices</ActionButton>}
              </Col>
              {!portalMode &&
                <Col>
                  <Checkbox id="lab_accounts_not_required" label="Lab Account Not Required"
                    className="m-2" disabled={disabled}
                    checked={!labAcctRequired} onChange={this.handleLabAccountsNotRequiredChange} />
                </Col>
              }
            </Form.Row>}
            {!portalMode && <>
              {isPortalAdmin && allowAdditionalRole &&
              <Form.Row className="form-group">
                <Form.Label column sm={4} htmlFor="portalAdminRole">Portal Admin Additional Role</Form.Label>
                <Col sm={8}>
                  <Form.Control id="portalAdminRole"
                    as="select"
                    disabled={rolesLoading || disabled}
                    value={portalAdminRoleID}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.setState({ portalAdminRoleID: Number(e.target.value) })}>
                    <option value="0">Select</option>
                    {roles?.map(role => <option value={role.RoleID} key={role.RoleID}>{role.Description}</option>)}
                  </Form.Control>
                </Col>
              </Form.Row>}
              <Form.Row className="form-group required">
                <Form.Label column sm={4} htmlFor="primaryIDType">Primary ID Type</Form.Label>
                <Col sm={8}>
                  <Form.Control id="primaryIDType"
                    as="select"
                    disabled={primaryIDTypesLoading || disabled}
                    value={primaryIDType}
                    isInvalid={!isPrimaryIDTypeValid}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.setState({ primaryIDType: Number(e.target.value), isPrimaryIDTypeValid: this.isPrimaryIDTypeValid(Number(e.target.value)) })}>
                    <option value="0">Select</option>
                    {primaryIDTypes?.map(item => <option value={item.PrimaryTypeID} key={item.PrimaryTypeID}>{item.PrimaryIDType}</option>)}
                  </Form.Control>
                </Col>
              </Form.Row>
            </>}
            {portalMode && !isNew && <>
              <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="primaryIDType">Add corporate Logo to Workforce Portal</Form.Label>
                <Col lg={8}>
                  <InputGroup>
                    <Form.Control type="text" defaultValue="" disabled className="bg-white" />
                    <InputGroup.Append>
                      <Button variant="secondary" disabled={disabled} onClick={() => this.setState({ showManageImages: true })}>Manage</Button>
                    </InputGroup.Append>
                  </InputGroup>
                  <em className="small">Ideal logo size should be 200 by 100 pixels and .jpg, .jpeg, .gif, .png file type</em>
                </Col>
              </Form.Row>
              <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="primaryIDType">Add corporate Logo for MRO Results</Form.Label>
                <Col lg={8}>
                  <InputGroup>
                    <Form.Control type="text" name="fileName" defaultValue={mroFileName || ""} readOnly className="bg-white" isInvalid={!isMROFileSizeValid} />
                    <InputGroup.Append>
                      <Button variant="secondary" onClick={() => { this.mroFile && this.mroFile.current && this.mroFile.current.click() }}>Browse</Button>
                    </InputGroup.Append>
                    <Form.Control.Feedback type="invalid">
                      The image size is invalid. Please resize the image size to less than {MaxImageFileSize / 1024} KB
                    </Form.Control.Feedback>
                  </InputGroup>
                  <em className="small">This option is only available for results reviewed by a Red Arrow integrated MRO</em>
                  <Form.Control type="file" ref={this.mroFile} accept=".bmp, .BMP" onChange={this.handleFileSelect} style={{ display: 'none' }} />
                </Col>
              </Form.Row>
            </>}
            {showAdditionalFields && <>
              <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="contactDerFirstName">
                  DER Contact First\Last Names
                  <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={
                      <Popover id="contactFirstNamePopover">
                        <ListGroup>
                          <ListGroup.Item>The above information will print in the DER  area of the Marketplace Authorization form for all Marketplace orders.</ListGroup.Item>
                        </ListGroup>
                      </Popover>}>
                      <FaInfoCircle className="info-icon" />
                    </OverlayTrigger>
                </Form.Label>
                <Col lg={4}>
                  <Form.Control id="contactDerFirstName" type="text"
                    autoComplete="new-password"
                    value={contactDerFirstName}
                    onChange={e => this.setState({ contactDerFirstName: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerFirstName} />
                </Col>
                <Col lg={4}>
                  <Form.Control id="contactDerLastName" type="text"
                    autoComplete="new-password"
                    value={contactDerLastName}
                    onChange={e => this.setState({ contactDerLastName: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerLastName} />
                </Col>
              </Form.Row>
              <Form.Row className="form-group required">
                <Form.Label column lg={4} htmlFor="contactDerPhone">
                  DER Contact Phone #
                  <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={
                      <Popover id="contactPhonePopover">
                        <ListGroup>
                          <ListGroup.Item>The above information will print in the DER  area of the Marketplace Authorization form for all Marketplace orders.</ListGroup.Item>
                        </ListGroup>
                      </Popover>}>
                      <FaInfoCircle className="info-icon" />
                    </OverlayTrigger>
                </Form.Label>
                <Col lg={8}>
                  <PhoneNumberMaskedInput
                    autoComplete="new-password"
                    value={contactDerPhone}
                    onChange={(value: string) => this.setState({ contactDerPhone: value })}
                    disabled={disabled}
                    isInvalid={!validContactDerPhone}
                    intl11={isIntl11} />
                </Col>
              </Form.Row>
              <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="contactDerEmail">
                  DER Contact Email #
                </Form.Label>
                <Col lg={8}>
                <Form.Control id="contactDerEmail" type="text"
                    autoComplete="new-password"
                    value={contactDerEmail}
                    onChange={e => this.setState({ contactDerEmail: e.target.value })}
                    disabled={disabled}
                    isInvalid={!validContactDerEmail} />
                </Col>
              </Form.Row>
            </>}
            {!isNew && !portalMode && <Form.Row className="form-group">
              <Form.Label column lg={4}>Locations</Form.Label>
              <Col lg={8}>
                <Button variant="secondary" onClick={() => this.setState({showLocation: true})} disabled={disabled}>Locations</Button>
                {isSiteAdmin && <Button variant="secondary" onClick={() => this.setState({showAdditionalTesting: true})} disabled={disabled} className="ml-2">Additional Testing</Button>}
              </Col>
            </Form.Row>}
            {isSiteAdmin && !portalMode && <Form.Row className="form-group">
              <Form.Label column lg={4}>Integration Type</Form.Label>
              <Col>
                <IntegrationTypeSelect value={integrationType} disabled={disabled} onChange={this.handleIntegrationTypeChange} />
              </Col>
            </Form.Row>}
            {portalMode && (isSiteAdmin || isPortalAdmin) &&
              <Form.Row className="form-group">
                  <Form.Label column lg={4}>Hide Auth form widget for ESS customers</Form.Label>
                  <Col>
                    <Checkbox id="hide_auth_for_ess" label=""
                      className="m-2" disabled={false}
                      checked={hideAuthESS} onChange={this.handleHideAuthChange} />
                  </Col>
              </Form.Row>}
              {portalMode && (isSiteAdmin || isPortalAdmin) &&
              <Form.Row className="form-group">
                  <Form.Label column lg={4}>Allow full Donor ID to print on CSV Export</Form.Label>
                  <Col>
                      <ToggleSwitch id="unmask_donor_id" className="mt-2" disabled={false} on="Yes" off="No" checked={unmaskDonorID > 0} onCheck={this.handleUnmackDonorIDChange} />
                  </Col>
              </Form.Row>}
            {portalMode && (isSiteAdmin || isPortalAdmin) &&
                <Form.Row className="form-group">
                <Form.Label column lg={4} htmlFor="generateResultExceptions">Turn Off Result Exceptions</Form.Label>
                <Col>
                    <ToggleSwitch id="generateResultExceptions" className="mt-2" disabled={isPortalAdmin} on="Yes" off="No" checked={generateResultExceptions} onCheck={this.handleGenerateResultExceptionsChange} />
                </Col>
            </Form.Row>}
            {portalMode && isSiteAdmin && <Form.Row className="form-group">
              <Form.Label column lg={4}>Show "Create Order Company" column</Form.Label>
              <Col>
                  <ToggleSwitch id="createOrderCompany" className="mt-2" disabled={false} on="Yes" off="No" checked={NoLabAccount > 0} onCheck={this.handleNoLabAccountChange} />
              </Col>
            </Form.Row>}
          </Form>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div>
            {canImport && <>
              <Button variant="primary" disabled={!portalData?.ImplementationID} onClick={() => this.setState({ showCompanyImport: true })} className="mr-2">Company Import</Button>
              <Button variant="primary" disabled={disabled} onClick={this.handleAddCompany}>Add Company</Button>
            </>}
            {canImportLocation && <Button variant="primary" disabled={!companyData?.AccountID} onClick={() => this.setState({ showLocationImport: true })} className="mr-2">Location Import</Button>}
            {!isNew && isPortalAdmin && portalMode &&
              <Button variant="primary" disabled={loadingCsv} onClick={this.handleCompaniesExport} className="ml-2">
                Companies Details Export
                {loadingCsv ? <Spinner animation="border" size="sm" /> : ""}
              </Button>
            }
          </div>
          <div>
            {!isNew && (isSiteAdmin || (isPortalAdmin && !portalMode)) && <>
              {!showActivateButton && <ActionButton variant="secondary" disabled={disabled} onClick={() => this.setState({ showInactivateConfirmation: true })} spin={inactivating} disableOnSpin className="mr-2">Inactivate</ActionButton>}
              {showActivateButton && <ActionButton variant="secondary" onClick={() => this.performActivate()} spin={activating} disableOnSpin className="mr-2">Activate</ActionButton>}
            </>}
            <Button variant="secondary" onClick={() => this.handleCancel()} className="mr-2">Cancel</Button>
            <ActionButton variant="primary" onClick={this.handleUpdate} disabled={!this.isFormValid() || disabled} spin={saving} disableOnSpin>{isNew ? "Save": "Update"}</ActionButton>
          </div>
        </Modal.Footer>
      </Modal.Dialog>}
      <ConfirmationModal show={this.state.showInactivateConfirmation}
        title="CONFIRM INACTIVATE"
        textTitle="Are you sure you want to inactivate?"
        text=""
        onConfirm={this.performInactivate}
        onHide={() => this.setState({ showInactivateConfirmation: false })} />
      <ConfirmationModal show={this.state.showNewCompanyModal}
          title="COMPANY WAS CREATED"
          textTitle=""
          text={`Please note that company ${this.props.match.params.companyName || ""} was created and Lab Accounts can be added now`}
          showImage={false}
          showCancelButton={false}
          showCloseButton={false}
          onConfirm={() => this.setState({ showNewCompanyModal: false})}
          onHide={() => this.setState({ showNewCompanyModal: false})} />
      {!portalMode && <AddressMatchModal
        show={!!retrievedCompanies && retrievedCompanies.length > 1}
        addresses={retrievedCompanies?.map(item => ({ ID: item.AccountID.toString(), Address1: item.Address1, Zip: item.Zip } as AddressData)) || []}
        selectedID={companyData?.AccountID.toString()}
        title="EXISTING COMPANY ADDRESS"
        subTitle="The company name you have entered already exists as the following addresses:"
        message="Please select the address you want to activate"
        onAddressSelect={(selected: AddressData) => this.setState({ companyData: this.state.retrievedCompanies?.find(item => item.AccountID.toString() === selected.ID) })}
        onClose={this.handleRetrievedCompanyAddressSelect} />}
      {showOrderSecurityOverride && <OrderSecurityOverride show={showOrderSecurityOverride}
        onClose={() => this.setState({ showOrderSecurityOverride: false })} />}
      {portalMode && <>
        <AddressMatchModal
          show={!!retrievedPortals && retrievedPortals.length > 1}
          addresses={retrievedPortals?.map(item => ({ ID: item.ImplementationID.toString(), Address1: item.Address1, Zip: item.Zip } as AddressData)) || []}
          selectedID={portalData?.ImplementationID.toString()}
          title="EXISTING PORTAL ADDRESS"
          subTitle="The clinic name you have entered already exists at the following addresses:"
          message="Please select the address you want to activate"
          onAddressSelect={(selected: AddressData) => this.setState({ portalData: this.state.retrievedPortals?.find(item => item.ImplementationID.toString() === selected.ID) })}
          onClose={this.handleRetrievedPortalAddressSelect} />
        <SubscriptionModal show={showSubscriptions}
          subscriptionPlanID={subscriptionPlanID}
          onCancel={() => this.setState({ showSubscriptions: false })}
          onSubmit={(subscriptionPlanID: number, subscriptionPlan: SubscriptionPlan) => this.setState({ showSubscriptions: false, subscriptionPlanID: subscriptionPlanID, subscriptionPlan: subscriptionPlan })} />
        {showManageImages && portalData?.ImplementationID && <ManageImages show={showManageImages} implementationID={portalData.ImplementationID} onClose={() => this.setState({ showManageImages: false })} />}
        {canImport && showCompanyImport && portalData?.ImplementationID && <CompanyImport portalID={portalData.ImplementationID} onClose={() => this.setState({ showCompanyImport: false })} />}
      </>}
      {canImportLocation && showLocationImport && !!companyData && <LocationImport portalID={companyData.ImplementationID} companyID={companyData.AccountID} onClose={() => this.setState({ showLocationImport: false })} />}
      {companyData && showLocation && <EditLocationInformation adminMode portalID={companyData.ImplementationID} companyID={companyData.AccountID} onCancel={() => { this.setState({ showLocation: false })}}/>}
      {companyData && showAdditionalTesting && <AdditionalTesting
        accountID={companyData.AccountID}
        implementationID={companyData.ImplementationID}
        show={this.state.showAdditionalTesting}
        onClose={() => { this.setState({ showAdditionalTesting: false })}} />}

    </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): CompanyInformationAuthProps {
  return {
    companyName: SelectedCompanyName(state),
    portalName: SelectedPortalName(state),
    portal: GetSelectedPortal(state),
    isCompanyAdmin: isCompanyAdmin(state),
    isPortalAdmin: isPortalAdmin(state),
    isSiteAdmin: isSiteAdmin(state),
    userAccess: BuildUserAccess(state),
    userName: getUserNameForSelectedCompany(state)
  }
}

function mapDispatchProps(dispatch: Dispatch<Action>): CompanyInformationDispatchProps {
  return {
    reload: () => (dispatch(WorkforceReloadPortalsCompanies(true)))
  }
}

const ConnectedCompanyInformation = connect(mapStateToProps, mapDispatchProps)(CompanyInformation)
export default ConnectedCompanyInformation
